import {
  queryOptions as tanstackQueryOptions,
  useQuery,
} from '@tanstack/react-query';
import { z } from 'zod';
import type { ApiResponse } from '@/types/api-types';
import type { QueryOptions } from '@/types/tanstack-query-options';
import type { paths } from '~/generated/cdr/api-schema';
import { zodRemoveNullable } from '@/lib/zod-helper';
import { cdrApi } from '@/services';
import { queryUserFavFuncResSchema } from '~/generated/cdr/zod-schema';
import { type MenuKey, menuKeySchema } from '../consts/menu';
import { QUERY_USER_FAV_FUNC_KEY } from './shared/user-fav-func-query-key';
import { useMutationDeleteUserFavFunc } from './use-mutation-delete-user-fav-func';

const API_PATH = QUERY_USER_FAV_FUNC_KEY;

type APIResponse = ApiResponse<paths, typeof API_PATH>;

const apiResponseSchema = z.array(zodRemoveNullable(queryUserFavFuncResSchema));

const queryFn = async (signal: AbortSignal): Promise<string[]> => {
  const { data: response } = await cdrApi.get<APIResponse>(API_PATH, {
    signal,
  });

  return apiResponseSchema.parse(response.data).map(({ feFuncId }) => feFuncId);
};

export const getQueryUserFavFuncOptions = () =>
  tanstackQueryOptions({
    queryKey: [API_PATH] as const,
    queryFn: async ({ signal }) => {
      const funcList = await queryFn(signal);

      return funcList.flatMap((func) => {
        const result = menuKeySchema.safeParse(func);

        return result.success ? [result.data] : [];
      });
    },
  });

export const useQueryUserFavFunc = (options?: QueryOptions<MenuKey[]>) => {
  const { mutateAsync: delUserFavFunc } = useMutationDeleteUserFavFunc();

  return useQuery({
    queryKey: [API_PATH],
    queryFn: async ({ signal }) => {
      const funcList = await queryFn(signal);

      const menuKeys: MenuKey[] = [];
      // Remove the invalid favorite function to prevent issues in future developer updates, such as name changes or route modifications.
      const invalidFunc: string[] = [];

      funcList.forEach((func) => {
        const result = menuKeySchema.safeParse(func);

        if (result.error) {
          invalidFunc.push(func);

          return;
        }

        menuKeys.push(result.data);
      });

      if (invalidFunc.length) {
        delUserFavFunc({ feFuncIds: invalidFunc });
      }

      return menuKeys;
    },
    ...options,
  });
};
